import { sentryCaptureException } from 'utils/sentry';
import { TURNSTILE_GRACEFUL_DEGRADATION } from 'config/errorTypes';
import { TURNSTILE_SITE_KEY } from 'config/envVariable';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_TEST_KEY_PATTERN = /^[123]x0{20}/;
// script 載入 + 背景取 token 的總逾時，超時視為降級（resolve null）
const HEADLESS_TIMEOUT_MS = 10000;

// Module-level Set：同一 session 內每把 siteKey 只回報一次 Sentry warning（與 Turnstile 元件分開計數）
const reportedSiteKeys = new Set();

function reportGracefulDegradation(siteKey, reason) {
    if (reportedSiteKeys.has(siteKey)) return;
    reportedSiteKeys.add(siteKey);
    sentryCaptureException({
        errType: TURNSTILE_GRACEFUL_DEGRADATION,
        extraInfo: {
            errType: TURNSTILE_GRACEFUL_DEGRADATION,
            reason: `headless:${reason}`,
            siteKey
        }
    });
}

function ensureScriptLoaded() {
    return new Promise((resolve, reject) => {
        if (window.turnstile) {
            resolve();
            return;
        }
        const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
        if (existing) {
            const waitForTurnstile = () => {
                if (window.turnstile) resolve();
                else setTimeout(waitForTurnstile, 50);
            };
            waitForTurnstile();
            return;
        }
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('script-load-error'));
        document.head.appendChild(script);
    });
}

/**
 * 無頭（headless）取得 Turnstile token — 供登入初始化等「非使用者互動、無可見 widget 容器」的
 * 自動呼叫路徑使用（#1 recover 舊帳號、#7 email 同步）。移植自 fever-admin AppPromoClient.js 的 resetTurnstile。
 *
 * 動態建立離螢幕（offscreen）<div> → window.turnstile.render()（interaction-only）→ 正常使用者背景即回傳 token。
 * 遇需互動 / script 被擋 / 逾時等例外則降級 resolve(null)；siteKey 未設定則 resolve(undefined)。
 * 呼叫端據回傳值決定是否帶 X-Cfts-Token；降級（null/undefined）時不帶，交由後端於無 token 時放行。
 *
 * @param {string} [siteKey=TURNSTILE_SITE_KEY]
 * @returns {Promise<string|null|undefined>} token / null（降級）/ undefined（未設定 sitekey）
 */
export default function getTurnstileTokenHeadless(siteKey = TURNSTILE_SITE_KEY) {
    // siteKey 未設定：靜默停用，不帶 token
    if (!siteKey) {
        return Promise.resolve(undefined);
    }

    return new Promise(resolve => {
        const isTestKey = TURNSTILE_TEST_KEY_PATTERN.test(siteKey);
        let settled = false;
        let container = null;
        let widgetId = null;
        let timeoutTimer = null;

        const cleanup = () => {
            if (timeoutTimer) {
                clearTimeout(timeoutTimer);
                timeoutTimer = null;
            }
            try {
                if (widgetId !== null && window.turnstile) {
                    window.turnstile.remove(widgetId);
                }
            } catch (e) {
                // ignore
            }
            if (container) {
                container.remove();
                container = null;
            }
        };

        const settle = (token, degradeReason) => {
            if (settled) return;
            settled = true;
            if (degradeReason) reportGracefulDegradation(siteKey, degradeReason);
            cleanup();
            resolve(token);
        };

        timeoutTimer = setTimeout(() => settle(null, 'timeout'), HEADLESS_TIMEOUT_MS);

        ensureScriptLoaded()
            .then(() => {
                if (settled) return;
                container = document.createElement('div');
                container.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
                document.body.appendChild(container);
                widgetId = window.turnstile.render(container, {
                    sitekey: siteKey,
                    callback: token => settle(token),
                    'error-callback': errorCode => settle(null, `error-callback:${errorCode}`),
                    'timeout-callback': () => settle(null, 'timeout-callback'),
                    appearance: isTestKey ? 'always' : 'interaction-only',
                    execution: 'render',
                    // 本函式為單次取 token（拿到即 remove widget），故不需過期自動重取。
                    'refresh-expired': 'never'
                });
            })
            .catch(() => settle(null, 'script-load-error'));
    });
}
